import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { apiRequest } from "../lib/api";
import type { LoginResult } from "../lib/api-types";
import { paths } from "../lib/endpoints";
import { rememberWorkspace } from "../lib/recent-workspaces";
import { Button } from "../components/ui/Button";
import { ErrorText } from "../components/ui/feedback";
import { meQueryKey } from "./queries";

export interface DevLoginFormProps {
  invite?: string | undefined;
}

/** Development-only sign-in by email. Shown only when the API offers dev login. */
export function DevLoginForm({ invite }: DevLoginFormProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");

  const login = useMutation({
    mutationFn: (value: string) =>
      apiRequest<LoginResult>(paths.devLogin, {
        method: "POST",
        body: invite ? { email: value, invite } : { email: value },
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      if (result.workspaceId) {
        rememberWorkspace(result.workspaceId);
        await navigate({
          to: "/app/$workspaceId/roster",
          params: { workspaceId: result.workspaceId },
        });
      } else {
        await navigate({ to: "/app" });
      }
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    login.mutate(email.trim());
  };

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-3 border-t border-line pt-4"
      aria-label={t("auth.dev.title")}
    >
      <h2 className="text-sm font-semibold text-muted">{t("auth.dev.title")}</h2>
      <label className="block space-y-1 text-sm">
        <span>{t("auth.dev.email")}</span>
        <input
          type="email"
          name="email"
          required
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="w-full"
        />
      </label>
      <ErrorText error={login.error} />
      <Button type="submit" variant="secondary" className="w-full" disabled={login.isPending}>
        {t("auth.dev.submit")}
      </Button>
    </form>
  );
}
